import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Sofa, Search, Check, X, ExternalLink, Loader2, ChevronLeft, ChevronRight,
  DollarSign, Clock, Tag, Package, MapPin, Filter, ThumbsUp, ThumbsDown, Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FURNITURE_TAGS = [
  { value: "chairs", label: "Chairs" },
  { value: "tables", label: "Tables" },
  { value: "sofas", label: "Sofas" },
  { value: "mirrors", label: "Mirrors" },
  { value: "lights", label: "Lights" },
  { value: "shelving", label: "Shelving" },
  { value: "counters", label: "Counters" },
  { value: "stools", label: "Stools" },
  { value: "benches", label: "Benches" },
  { value: "plants", label: "Plants" },
  { value: "artwork", label: "Artwork" },
  { value: "rugs", label: "Rugs" },
  { value: "storage", label: "Storage" },
  { value: "other", label: "Other" },
];

const STATUS_STYLES = {
  potential: { bg: "bg-stone-100", text: "text-stone-600", label: "Potential" },
  selected: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Selected" },
  rejected: { bg: "bg-red-100", text: "text-red-700", label: "Rejected" },
  ordered: { bg: "bg-violet-100", text: "text-violet-700", label: "Ordered" },
  delivered: { bg: "bg-blue-100", text: "text-blue-700", label: "Delivered" },
};

export default function PublicFurniture() {
  const { shareToken } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  
  // Filters
  const [filterTag, setFilterTag] = useState(searchParams.get("tag") || "all");
  const [filterStatus, setFilterStatus] = useState(searchParams.get("status") || "all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("name"); // name, price, lead_time
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });
  const [leadTimeMax, setLeadTimeMax] = useState("");
  
  // Detail modal
  const [selectedItem, setSelectedItem] = useState(null);
  const [voting, setVoting] = useState(false);
  
  // Quantity selection modal
  const [quantityModalItem, setQuantityModalItem] = useState(null);
  const [selectedQty, setSelectedQty] = useState(1);

  useEffect(() => {
    fetchData();
  }, [shareToken, filterTag, filterStatus]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterTag !== "all") params.tag = filterTag;
      if (filterStatus !== "all") params.status = filterStatus;
      
      const res = await axios.get(`${API}/public/furniture/${shareToken}`, { params });
      setData(res.data);
      document.title = `${res.data.store_name || "Furniture"} — Command Center`;
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "This furniture collection is not available");
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (itemId, newStatus, quantity = null) => {
    if (!data?.allow_voting) return;
    setVoting(true);
    try {
      const payload = { status: newStatus };
      if (quantity !== null) {
        payload.selected_quantity = quantity;
      }
      await axios.patch(`${API}/public/furniture/${shareToken}/${itemId}/vote`, payload);
      fetchData();
      if (selectedItem?.id === itemId) {
        setSelectedItem(prev => ({ ...prev, status: newStatus, selected_quantity: quantity }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setVoting(false);
      setQuantityModalItem(null);
    }
  };

  const openQuantityModal = (item) => {
    setQuantityModalItem(item);
    setSelectedQty(item.selected_quantity || 1);
  };

  const confirmSelection = () => {
    if (quantityModalItem) {
      handleVote(quantityModalItem.id, "selected", selectedQty);
    }
  };

  // Apply client-side filters
  const filteredItems = (data?.furniture || []).filter(item => {
    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!item.name?.toLowerCase().includes(q) && 
          !item.vendor?.toLowerCase().includes(q) &&
          !item.description?.toLowerCase().includes(q)) {
        return false;
      }
    }
    // Price range filter
    if (priceRange.min && (item.price || 0) < parseFloat(priceRange.min)) return false;
    if (priceRange.max && (item.price || 0) > parseFloat(priceRange.max)) return false;
    // Lead time filter
    if (leadTimeMax && (item.lead_time_weeks || 0) > parseInt(leadTimeMax)) return false;
    return true;
  }).sort((a, b) => {
    if (sortBy === "price") return (a.price || 0) - (b.price || 0);
    if (sortBy === "lead_time") return (a.lead_time_weeks || 0) - (b.lead_time_weeks || 0);
    return (a.name || "").localeCompare(b.name || "");
  });

  // Stats
  const selectedItems = filteredItems.filter(i => i.status === "selected");
  const stats = {
    total: filteredItems.length,
    selected: selectedItems.length,
    rejected: filteredItems.filter(i => i.status === "rejected").length,
    potential: filteredItems.filter(i => i.status === "potential").length,
    totalCost: filteredItems.reduce((sum, i) => sum + (i.price || 0) * (i.quantity || 1), 0),
    selectedCost: selectedItems.reduce((sum, i) => sum + (i.price || 0) * (i.selected_quantity || i.quantity || 1), 0),
    longestSelectedLeadTime: selectedItems.length > 0 
      ? Math.max(...selectedItems.map(i => i.lead_time_weeks || 0))
      : 0,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400 mx-auto mb-3" />
          <p className="text-stone-500">Loading furniture collection...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100 flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <Sofa className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-stone-800 mb-2">Collection Not Found</h1>
            <p className="text-stone-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-amber-50/30 to-stone-100">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-stone-900 via-stone-800 to-stone-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12 sm:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
              <Sofa className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <p className="text-amber-400/80 text-sm font-medium tracking-wide uppercase">Furniture Selection</p>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                {data.store_name}
              </h1>
            </div>
          </div>
          {data.store?.address && (
            <p className="text-stone-400 flex items-center gap-2 mb-6">
              <MapPin className="w-4 h-4" /> {data.store.address}
            </p>
          )}
          
          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 sm:gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-stone-400 text-xs uppercase tracking-wide mb-1">Total Items</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-stone-400 text-xs uppercase tracking-wide mb-1">Selected</p>
              <p className="text-2xl font-bold text-emerald-400">{stats.selected}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
              <p className="text-stone-400 text-xs uppercase tracking-wide mb-1">Selected Value</p>
              <p className="text-2xl font-bold text-emerald-400">${stats.selectedCost.toLocaleString()}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 col-span-2 sm:col-span-1">
              <p className="text-stone-400 text-xs uppercase tracking-wide mb-1">Longest Lead Time</p>
              <p className="text-2xl font-bold text-amber-400">
                {stats.longestSelectedLeadTime > 0 ? `${stats.longestSelectedLeadTime} wks` : "—"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-stone-200/60 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Search furniture..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-white border-stone-200"
              />
            </div>
            
            {/* Tag Filter */}
            <Select value={filterTag} onValueChange={setFilterTag}>
              <SelectTrigger className="w-32 bg-white">
                <Tag className="w-3.5 h-3.5 mr-1.5 text-stone-400" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {FURNITURE_TAGS.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Status Filter */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-32 bg-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="potential">Potential</SelectItem>
                <SelectItem value="selected">Selected</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Price Range */}
            <div className="flex items-center gap-1">
              <DollarSign className="w-4 h-4 text-stone-400" />
              <Input
                type="number"
                placeholder="Min"
                value={priceRange.min}
                onChange={(e) => setPriceRange(p => ({ ...p, min: e.target.value }))}
                className="w-20 bg-white"
              />
              <span className="text-stone-400">-</span>
              <Input
                type="number"
                placeholder="Max"
                value={priceRange.max}
                onChange={(e) => setPriceRange(p => ({ ...p, max: e.target.value }))}
                className="w-20 bg-white"
              />
            </div>
            
            {/* Lead Time */}
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-stone-400" />
              <Input
                type="number"
                placeholder="Max wks"
                value={leadTimeMax}
                onChange={(e) => setLeadTimeMax(e.target.value)}
                className="w-24 bg-white"
              />
            </div>
            
            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-32 bg-white">
                <SelectValue placeholder="Sort" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="price">Price</SelectItem>
                <SelectItem value="lead_time">Lead Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {data.allow_voting && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              <span className="font-medium">Your feedback is welcome!</span> Use the Select or Reject buttons on each item, or click on an item for more details.
            </p>
          </div>
        )}

        {filteredItems.length === 0 ? (
          <Card className="bg-white/60 backdrop-blur-sm border-stone-200/60">
            <CardContent className="py-16 text-center">
              <Sofa className="w-12 h-12 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">No furniture items match your filters.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filteredItems.map(item => {
              const statusStyle = STATUS_STYLES[item.status] || STATUS_STYLES.potential;
              return (
                <Card 
                  key={item.id}
                  onClick={() => setSelectedItem(item)}
                  className="group bg-white/80 backdrop-blur-sm border-stone-200/60 rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-stone-200/50 transition-all duration-300 cursor-pointer hover:-translate-y-1"
                >
                  {/* Image */}
                  <div className="relative aspect-[4/3] bg-gradient-to-br from-stone-100 to-stone-50 overflow-hidden">
                    {item.images?.length > 0 ? (
                      <img 
                        src={`${item.images[0].url}`}
                        alt={item.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Sofa className="w-16 h-16 text-stone-200" />
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    <div className="absolute top-3 left-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                        {statusStyle.label}
                      </span>
                    </div>
                    
                    {/* Price Badge */}
                    {item.price && (
                      <div className="absolute top-3 right-3">
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold bg-white/90 backdrop-blur-sm text-stone-900 shadow-lg">
                          ${item.price.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Content */}
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-stone-900 text-lg mb-1 truncate">{item.name}</h3>
                    {item.vendor && (
                      <p className="text-sm text-stone-500 mb-3">{item.vendor}</p>
                    )}
                    
                    {/* Tags */}
                    {item.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {item.tags.slice(0, 3).map(tag => (
                          <span key={tag} className="text-xs px-2 py-0.5 bg-stone-100 text-stone-600 rounded-full">
                            {FURNITURE_TAGS.find(t => t.value === tag)?.label || tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Meta Info */}
                    <div className="flex items-center gap-4 text-xs text-stone-500">
                      {item.quantity > 1 && (
                        <span className="flex items-center gap-1">
                          <Package className="w-3.5 h-3.5" /> 
                          {item.status === "selected" && item.selected_quantity 
                            ? `${item.selected_quantity}/${item.quantity} selected`
                            : `Qty: ${item.quantity}`
                          }
                        </span>
                      )}
                      {item.lead_time_weeks && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" /> {item.lead_time_weeks} wks
                        </span>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-stone-100" onClick={(e) => e.stopPropagation()}>
                      {data.allow_voting && item.status !== "ordered" && item.status !== "delivered" && (
                        <>
                          {item.status === "selected" ? (
                            // Show edit qty + de-select buttons when selected
                            <>
                              <Button
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); openQuantityModal(item); }}
                                disabled={voting}
                                variant="outline"
                                className="flex-1 h-8 text-xs bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                              >
                                <Check className="w-3.5 h-3.5 mr-1" /> Selected ({item.selected_quantity || item.quantity || 1})
                              </Button>
                              <Button
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleVote(item.id, "potential"); }}
                                disabled={voting}
                                variant="outline"
                                className="h-8 text-xs text-stone-500 hover:bg-stone-100"
                              >
                                <X className="w-3.5 h-3.5" />
                              </Button>
                            </>
                          ) : (
                            // Show select button when not selected
                            <Button
                              size="sm"
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                openQuantityModal(item);
                              }}
                              disabled={voting}
                              className="flex-1 h-8 text-xs bg-emerald-500 hover:bg-emerald-600 text-white"
                            >
                              <ThumbsUp className="w-3.5 h-3.5 mr-1" /> Select
                            </Button>
                          )}
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleVote(item.id, "rejected"); }}
                            disabled={voting || item.status === "rejected"}
                            variant="outline"
                            className={`flex-1 h-8 text-xs ${item.status === "rejected" ? "bg-red-50 border-red-200 text-red-700" : "hover:bg-red-50 hover:border-red-200 hover:text-red-700"}`}
                          >
                            {item.status === "rejected" ? (
                              <><X className="w-3.5 h-3.5 mr-1" /> Rejected</>
                            ) : (
                              <><ThumbsDown className="w-3.5 h-3.5 mr-1" /> Reject</>
                            )}
                          </Button>
                        </>
                      )}
                      {item.url && (
                        <a href={item.url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={data.allow_voting && item.status !== "ordered" && item.status !== "delivered" ? "w-full" : "flex-1"}>
                          <Button size="sm" variant="outline" className="w-full h-8 text-xs">
                            <ExternalLink className="w-3.5 h-3.5 mr-1" /> View Product
                          </Button>
                        </a>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selectedItem} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          {selectedItem && (
            <>
              {/* Image Gallery */}
              <div className="relative aspect-video bg-gradient-to-br from-stone-100 to-stone-50">
                {selectedItem.images?.length > 0 ? (
                  <img 
                    src={`${selectedItem.images[0].url}`}
                    alt={selectedItem.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Sofa className="w-20 h-20 text-stone-200" />
                  </div>
                )}
                
                {/* Status Badge */}
                <div className="absolute top-4 left-4">
                  <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${STATUS_STYLES[selectedItem.status]?.bg} ${STATUS_STYLES[selectedItem.status]?.text}`}>
                    {STATUS_STYLES[selectedItem.status]?.label}
                  </span>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {selectedItem.name}
                    </h2>
                    {selectedItem.vendor && (
                      <p className="text-stone-500 mt-1">{selectedItem.vendor}</p>
                    )}
                  </div>
                  {selectedItem.price && (
                    <div className="text-right">
                      <p className="text-3xl font-bold text-stone-900">${selectedItem.price.toLocaleString()}</p>
                    </div>
                  )}
                </div>
                
                {selectedItem.description && (
                  <p className="text-stone-600 mb-6">{selectedItem.description}</p>
                )}
                
                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  {selectedItem.dimensions && (
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Dimensions</p>
                      <p className="font-medium text-stone-900">{selectedItem.dimensions}</p>
                    </div>
                  )}
                  {selectedItem.material && (
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Material</p>
                      <p className="font-medium text-stone-900">{selectedItem.material}</p>
                    </div>
                  )}
                  {selectedItem.color && (
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Color</p>
                      <p className="font-medium text-stone-900">{selectedItem.color}</p>
                    </div>
                  )}
                  {selectedItem.lead_time_weeks && (
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">Lead Time</p>
                      <p className="font-medium text-stone-900">{selectedItem.lead_time_weeks} weeks</p>
                    </div>
                  )}
                </div>
                
                {/* Tags */}
                {selectedItem.tags?.length > 0 && (
                  <div className="mb-6">
                    <p className="text-xs text-stone-500 uppercase tracking-wide mb-2">Categories</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedItem.tags.map(tag => (
                        <span key={tag} className="px-3 py-1 bg-stone-100 text-stone-700 rounded-full text-sm">
                          {FURNITURE_TAGS.find(t => t.value === tag)?.label || tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Notes */}
                {selectedItem.notes && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <p className="text-xs text-amber-700 uppercase tracking-wide mb-1">Notes</p>
                    <p className="text-amber-900">{selectedItem.notes}</p>
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex flex-wrap gap-3 pt-4 border-t border-stone-200">
                  {data.allow_voting && selectedItem.status !== "ordered" && selectedItem.status !== "delivered" && (
                    <>
                      <Button
                        onClick={() => handleVote(selectedItem.id, "selected")}
                        disabled={voting || selectedItem.status === "selected"}
                        className={`flex-1 ${selectedItem.status === "selected" ? "bg-emerald-600" : "bg-emerald-500 hover:bg-emerald-600"} text-white`}
                      >
                        {voting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ThumbsUp className="w-4 h-4 mr-2" />}
                        {selectedItem.status === "selected" ? "Selected" : "Select This"}
                      </Button>
                      <Button
                        onClick={() => handleVote(selectedItem.id, "rejected")}
                        disabled={voting || selectedItem.status === "rejected"}
                        variant="outline"
                        className={`flex-1 ${selectedItem.status === "rejected" ? "bg-red-50 border-red-200 text-red-700" : "hover:bg-red-50 hover:border-red-200 hover:text-red-700"}`}
                      >
                        {voting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ThumbsDown className="w-4 h-4 mr-2" />}
                        {selectedItem.status === "rejected" ? "Rejected" : "Reject"}
                      </Button>
                    </>
                  )}
                  {selectedItem.url && (
                    <a href={selectedItem.url} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" className="w-full">
                        <ExternalLink className="w-4 h-4 mr-2" /> View Product
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Quantity Selection Modal */}
      <Dialog open={!!quantityModalItem} onOpenChange={(open) => !open && setQuantityModalItem(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Playfair Display', serif" }}>
              Select Quantity
            </DialogTitle>
            <p className="text-sm text-stone-500">Choose how many items to select</p>
          </DialogHeader>
          {quantityModalItem && (
            <div className="py-4">
              <p className="text-sm text-stone-600 mb-4">
                How many <span className="font-medium">{quantityModalItem.name}</span> would you like to select?
              </p>
              <p className="text-xs text-stone-500 mb-2">Available: {quantityModalItem.quantity}</p>
              
              {/* Quantity selector */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedQty(q => Math.max(1, q - 1))}
                  disabled={selectedQty <= 1}
                  className="w-10 h-10"
                >
                  -
                </Button>
                <span className="text-3xl font-bold text-stone-900 w-16 text-center">{selectedQty}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedQty(q => Math.min(quantityModalItem.quantity, q + 1))}
                  disabled={selectedQty >= quantityModalItem.quantity}
                  className="w-10 h-10"
                >
                  +
                </Button>
              </div>
              
              {/* Total price */}
              {quantityModalItem.price && (
                <div className="text-center mb-6 p-3 bg-stone-50 rounded-lg">
                  <p className="text-xs text-stone-500 mb-1">Total for selected quantity</p>
                  <p className="text-2xl font-bold text-stone-900">
                    ${(quantityModalItem.price * selectedQty).toLocaleString()}
                  </p>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={confirmSelection}
                  disabled={voting}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  {voting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Confirm Selection
                </Button>
                <Button variant="outline" onClick={() => setQuantityModalItem(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
